import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  applyAction,
  botAction,
  createGame,
  legalActions,
  viewFor,
  waitingOn,
  type Action,
  type GameEvent,
  type GameState,
  type RulesConfig,
} from '../engine';
import type { GameClient, LogEntry } from './game-client';
import { describeEvent } from './log';

const STORAGE_KEY = 'cyp.local.v2';
const LOG_LIMIT = 300;
const UNDO_LIMIT = 30;
/** Pause before a computer player moves, so people can follow along. */
export const BOT_DELAY_MS = 650;
/** Everyone chooses their 6 at once, so bots needn't make people wait. */
const BOT_TRIM_DELAY_MS = 120;

export interface LocalSetup {
  names: string[];
  bots: boolean[];
  rules: Partial<RulesConfig>;
  seed: number;
  /** Cover the screen between human seats (only matters with 2+ humans). */
  privacy: boolean;
  /** Computers move quickly (handy when only computers are playing). */
  fast?: boolean;
}

const SETUP_KEY = 'cyp.local.setup';

/** The last table set up on this device, so the next test is one tap. */
export function loadLastSetup(): Omit<LocalSetup, 'seed'> | null {
  try {
    const raw = localStorage.getItem(SETUP_KEY);
    return raw ? (JSON.parse(raw) as Omit<LocalSetup, 'seed'>) : null;
  } catch {
    return null;
  }
}

export function saveLastSetup(setup: LocalSetup): void {
  try {
    const { seed: _seed, ...rest } = setup;
    localStorage.setItem(SETUP_KEY, JSON.stringify(rest));
  } catch {
    // Not remembered; fine.
  }
}

export interface LocalSave {
  setup: LocalSetup;
  state: GameState;
  log: LogEntry[];
  /** States before each human move, newest last. */
  undo: { state: GameState; logLength: number }[];
  /** The human seat the screen is showing. */
  shown: number;
}

export function newLocalGame(setup: LocalSetup): LocalSave {
  const state = createGame({ playerCount: setup.names.length, seed: setup.seed, rules: setup.rules });
  const firstHuman = Math.max(0, setup.bots.findIndex((b) => !b));
  return { setup, state, log: [{ id: 0, text: `— Hand 1: ${setup.names[0]} deals —` }], undo: [], shown: firstHuman };
}

export function loadLocalGame(): LocalSave | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LocalSave) : null;
  } catch {
    return null;
  }
}

export function saveLocalGame(save: LocalSave | null): void {
  try {
    if (save) localStorage.setItem(STORAGE_KEY, JSON.stringify(save));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage full or blocked; the game still works, it just won't survive a refresh.
  }
}

/** `?fast=1` speeds bots up for tests; the setup's `fast` is the speed switch people use. */
function botDelay(state: GameState, setup: LocalSetup): number {
  if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('fast')) return 20;
  if (setup.fast) return state.phase.kind === 'trim' ? 40 : 180;
  return state.phase.kind === 'trim' ? BOT_TRIM_DELAY_MS : BOT_DELAY_MS;
}

/** Computer seats move before people during the simultaneous trim, so the screen settles on a human. */
function nextActor(state: GameState, bots: readonly boolean[]): number | null {
  const seats = waitingOn(state);
  return seats.find((s) => bots[s]) ?? seats[0] ?? null;
}

function appendLog(log: LogEntry[], events: GameEvent[], names: readonly string[]): LogEntry[] {
  let id = (log[log.length - 1]?.id ?? 0) + 1;
  const lines = events.flatMap((e) => {
    const text = describeEvent(e, names);
    return text ? [{ id: id++, text }] : [];
  });
  return [...log, ...lines].slice(-LOG_LIMIT);
}

export interface LocalClient extends GameClient {
  save: LocalSave;
  actor: number | null;
  /** Only computers are playing: the screen is a spectator. */
  watching: boolean;
  setFast(fast: boolean): void;
}

export function useLocalGame(initial: LocalSave): LocalClient {
  const [save, setSave] = useState(initial);
  // The table holds the computers while it shows a hand's result and the deal.
  const [held, setHeld] = useState(false);
  const { setup, state } = save;
  const actor = nextActor(state, setup.bots);
  const humanTurn = actor !== null && !setup.bots[actor];
  const watching = setup.bots.every(Boolean);
  // Show the acting human; while a bot thinks, keep showing whoever was last.
  const seat = watching ? null : humanTurn ? actor : save.shown;

  const commit = (next: LocalSave) => {
    saveLocalGame(next);
    setSave(next);
  };

  const apply = (from: LocalSave, player: number, action: Action, human: boolean): LocalSave | string => {
    const result = applyAction(from.state, player, action);
    if (!result.ok) return result.error;
    return {
      ...from,
      state: result.state,
      log: appendLog(from.log, result.events, from.setup.names),
      undo: human ? [...from.undo, { state: from.state, logLength: from.log.length }].slice(-UNDO_LIMIT) : from.undo,
      shown: human ? player : from.shown,
    };
  };

  useEffect(() => {
    if (held || actor === null || !setup.bots[actor]) return;
    const timer = setTimeout(() => {
      const move = botAction(viewFor(state, actor), legalActions(state, actor));
      if (!move) return;
      const next = apply(save, actor, move, false);
      if (typeof next !== 'string') commit(next);
    }, botDelay(state, setup));
    return () => clearTimeout(timer);
  }, [state, actor, setup.fast, held]);

  // During the trim everyone chooses at once, so the shown person may act
  // even while computer players are still choosing.
  const canAct = seat !== null && waitingOn(state).includes(seat) && !setup.bots[seat];
  const view = useMemo(() => viewFor(state, seat), [state, seat]);
  const legal = useMemo(() => (canAct ? legalActions(state, seat!) : []), [state, seat, canAct]);

  const act = (action: Action): string | null => {
    if (!canAct || seat === null) return 'wait for your turn';
    const next = apply(save, seat, action, true);
    if (typeof next === 'string') return next;
    commit(next);
    return null;
  };

  const last = save.undo[save.undo.length - 1];
  const undo = last
    ? () => commit({ ...save, state: last.state, log: save.log.slice(0, last.logLength), undo: save.undo.slice(0, -1) })
    : undefined;

  const setFast = (fast: boolean) => commit({ ...save, setup: { ...save.setup, fast } });

  return {
    view,
    seat,
    legal,
    names: setup.names,
    bots: setup.bots,
    log: save.log,
    act,
    hotSeat: true,
    save,
    actor,
    watching,
    setFast,
    holdBots: setHeld,
    ...(undo ? { undo } : {}),
  };
}
